import React, { useEffect, useMemo, useState } from 'react';
import { EyeIcon, PencilIcon } from '@heroicons/react/16/solid';
import { Heading } from '../../../components/Heading';
import { Button } from '../../../components/Button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../../components/Table';
import dateFormat from 'dateformat';
import { useNavigate } from 'react-router-dom';
import showErrorToast from '../../../components/Toasts/ShowErrorToast';
import Loading from '../../../components/Loading';
import { AndroidRelease, AppReleaseService } from '../../../services/appReleaseService';
import { Badge } from '../../../components/Badge';
import Pagination from '../../../components/Pagination';
import { Card } from '../../../components/Card';
import { ListToolbarRow, ListToolbarSearch, FiltersButton, SortMenu, ColumnsMenu, isColumnVisible } from '../../../components/ListToolbar';
import { FiltersDialog, FilterPills, FilterPill } from '../../../components/FiltersDialog';
import { DateRangePicker } from '../../../components/DateRangePicker';
import { Select } from '../../../components/Select';
import { Text } from '../../../components/Text';

const COLUMNS = [
    { key: 'code', label: 'Code' },
    { key: 'file', label: 'File' },
    { key: 'size', label: 'Size' },
    { key: 'status', label: 'Status' },
    { key: 'created', label: 'Created On' },
];
const SORT_OPTIONS = [
    { label: 'Newest first', value: 'newest' },
    { label: 'Oldest first', value: 'oldest' },
];

const AppUpdatesPage: React.FC = () => {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [releases, setReleases] = useState<AndroidRelease[]>([]);
    const [searchQuery, setSearchQuery] = useState('');
    const [currentPage, setCurrentPage] = useState<number>(1);
    const perPage = 10;
    const [sortOrder, setSortOrder] = useState('newest');
    const [columnVisibility, setColumnVisibility] = useState<Record<string, boolean>>({});
    const toggleColumn = (key: string) => setColumnVisibility((prev) => ({ ...prev, [key]: prev[key] === false ? true : false }));
    const [isFilterOpen, setIsFilterOpen] = useState(false);
    const [filterStartDate, setFilterStartDate] = useState('');
    const [filterEndDate, setFilterEndDate] = useState('');
    const [filterYear, setFilterYear] = useState('');
    const [filterStatus, setFilterStatus] = useState('');

    useEffect(() => {
        document.title = 'Mobile App Updates';
        void loadData();
    }, []);

    useEffect(() => {
        setCurrentPage(1);
    }, [searchQuery, filterStartDate, filterEndDate, filterYear, filterStatus]);

    const loadData = async () => {
        try {
            setLoading(true);
            const history = await AppReleaseService.getAndroidReleaseHistory(100);
            setReleases(history);
        } catch (error: any) {
            showErrorToast(error?.response?.data?.message || 'Failed to load releases');
        } finally {
            setLoading(false);
        }
    };

    const resetFilters = () => {
        setFilterStartDate('');
        setFilterEndDate('');
        setFilterYear('');
        setFilterStatus('');
    };

    const availableYears = Array.from(
        new Set(
            releases
                .map((r) => new Date(r.created_time).getFullYear())
                .filter((year) => !Number.isNaN(year))
        )
    ).sort((a, b) => b - a);

    const activeFilterCount = [filterStartDate, filterEndDate, filterYear, filterStatus].filter(Boolean).length;

    const filterPills: FilterPill[] = [
        ...(filterStartDate ? [{ key: 'from', label: `From: ${filterStartDate}`, onClear: () => setFilterStartDate('') }] : []),
        ...(filterEndDate ? [{ key: 'to', label: `To: ${filterEndDate}`, onClear: () => setFilterEndDate('') }] : []),
        ...(filterYear ? [{ key: 'year', label: `Year: ${filterYear}`, onClear: () => setFilterYear('') }] : []),
        ...(filterStatus ? [{ key: 'status', label: `Status: ${filterStatus === '1' ? 'Active' : 'Inactive'}`, onClear: () => setFilterStatus('') }] : []),
    ];

    const filteredReleases = useMemo(() => {
        const q = searchQuery.trim().toLowerCase();
        return releases.filter((r) => {
            const matchesSearch = !q ||
                r.version_name.toLowerCase().includes(q) ||
                String(r.version_code).includes(q) ||
                r.file_name.toLowerCase().includes(q);
            const matchesStartDate = !filterStartDate || new Date(r.created_time) >= new Date(filterStartDate);
            const matchesEndDate = !filterEndDate || new Date(r.created_time) <= new Date(filterEndDate);
            const matchesYear = !filterYear || new Date(r.created_time).getFullYear().toString() === filterYear;
            const matchesStatus = !filterStatus || r.is_active.toString() === filterStatus;
            return matchesSearch && matchesStartDate && matchesEndDate && matchesYear && matchesStatus;
        });
    }, [releases, searchQuery, filterStartDate, filterEndDate, filterYear, filterStatus]);

    const sortedReleases = useMemo(() => [...filteredReleases].sort((a, b) => {
        const diff = new Date(a.created_time).getTime() - new Date(b.created_time).getTime();
        return sortOrder === 'newest' ? -diff : diff;
    }), [filteredReleases, sortOrder]);
    const total = sortedReleases.length;
    const pageReleases = sortedReleases.slice((currentPage - 1) * perPage, currentPage * perPage);

    const formatFileSize = (bytes: number) => {
        if (!bytes || bytes <= 0) return '-';
        const mb = bytes / (1024 * 1024);
        return `${mb.toFixed(2)} MB`;
    };

    return (
        <>
            <div className="flex flex-wrap items-end justify-between gap-4">
                <div className="max-sm:w-full sm:flex-1">
                    <Heading>App Updates</Heading>
                </div>
                <Button onClick={() => navigate('/gp/app/updates/add')}>Upload new APK</Button>
            </div>

            <Card className="mt-6 p-5">
            <ListToolbarRow search={<ListToolbarSearch value={searchQuery} onChange={setSearchQuery} placeholder="Search version/file..." />}>
                <FiltersButton count={activeFilterCount} onClick={() => setIsFilterOpen(true)} />
                <SortMenu options={SORT_OPTIONS} value={sortOrder} onChange={setSortOrder} />
                <ColumnsMenu columns={COLUMNS} visible={columnVisibility} onToggle={toggleColumn} />
            </ListToolbarRow>
            <FilterPills pills={filterPills} onClearAll={resetFilters} />
            <ul>
                {loading && <div className="w-full text-center h-full py-36 justify-center"><Loading /></div>}
                {!loading && (
                    <div>
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableHeader>S/N</TableHeader>
                                    <TableHeader>Version</TableHeader>
                                    {isColumnVisible(columnVisibility, 'code') && <TableHeader>Code</TableHeader>}
                                    {isColumnVisible(columnVisibility, 'file') && <TableHeader>File</TableHeader>}
                                    {isColumnVisible(columnVisibility, 'size') && <TableHeader>Size</TableHeader>}
                                    {isColumnVisible(columnVisibility, 'status') && <TableHeader>Status</TableHeader>}
                                    {isColumnVisible(columnVisibility, 'created') && <TableHeader>Created On</TableHeader>}
                                    <TableHeader className="text-center">Actions</TableHeader>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {pageReleases.length > 0 ? pageReleases.map((release, index) => (
                                    <TableRow key={release.id}>
                                        <TableCell>{(currentPage - 1) * perPage + index + 1}</TableCell>
                                        <TableCell>{release.version_name}</TableCell>
                                        {isColumnVisible(columnVisibility, 'code') && <TableCell>{release.version_code}</TableCell>}
                                        {isColumnVisible(columnVisibility, 'file') && <TableCell>{release.file_name}</TableCell>}
                                        {isColumnVisible(columnVisibility, 'size') && <TableCell>{formatFileSize(release.file_size)}</TableCell>}
                                        {isColumnVisible(columnVisibility, 'status') && <TableCell>
                                            {release.is_active === 1
                                                ? <Badge color="green">Active</Badge>
                                                : <Badge color="zinc">Inactive</Badge>}
                                        </TableCell>}
                                        {isColumnVisible(columnVisibility, 'created') && <TableCell>{dateFormat(release.created_time, 'mmm dd, yyyy')}</TableCell>}
                                        <TableCell className="py-5 flex gap-4 text-zinc-500 justify-center">
                                            <a onClick={() => navigate(`/gp/app/updates/edit/${release.id}`)} className="text-zinc-600 hover:text-zinc-900">
                                                <PencilIcon className='h-5 w-5' />
                                            </a>
                                            <a onClick={() => navigate(`/gp/app/updates/view/${release.id}`)} className="text-zinc-600 hover:text-zinc-900">
                                                <EyeIcon className='h-5 w-5' />
                                            </a>
                                        </TableCell>
                                    </TableRow>
                                )) : (
                                    <TableRow>
                                        <TableCell colSpan={8} className="text-center py-8">No app release found.</TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                        <Pagination
                            currentPage={currentPage}
                            totalPages={Math.ceil(total / perPage)}
                            onPageChange={setCurrentPage}
                            totalResult={total}
                            dataName={'App Updates'}
                        />
                    </div>
                )}
            </ul>
            </Card>

            <FiltersDialog open={isFilterOpen} onClose={() => setIsFilterOpen(false)} onReset={resetFilters}>
                <div className="sm:col-span-2">
                    <Text>Date Range</Text>
                    <DateRangePicker
                        from={filterStartDate}
                        to={filterEndDate}
                        onChange={({ from, to }) => { setFilterStartDate(from); setFilterEndDate(to); }}
                        className="w-full"
                    />
                </div>
                <div>
                    <Text>By Year</Text>
                    <Select value={filterYear} onChange={(e) => setFilterYear(e.target.value)}>
                        <option value="">All Years</option>
                        {availableYears.map((year) => (
                            <option key={year} value={year.toString()}>{year}</option>
                        ))}
                    </Select>
                </div>
                <div>
                    <Text>By Status</Text>
                    <Select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}>
                        <option value="">All Statuses</option>
                        <option value="1">Active</option>
                        <option value="0">Inactive</option>
                    </Select>
                </div>
            </FiltersDialog>
        </>
    );
};

export default AppUpdatesPage;
